import { Buffer } from "buffer";
import { Auth } from "aws-amplify";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { jwtDecode } from "jwt-decode";
import {API} from "./API";
/**
 * ✅ Sign up a new user
 * @param email - User's email
 * @param password - User's password
 * @param attributes - Additional attributes (e.g., name, phone_number)
 */
export const signUp = async (email: string, password: string, attributes = {}) => {
    try {
        const response = await Auth.signUp({
            username: email,
            password,
            attributes, // e.g., { name: "John Doe", phone_number: "+1234567890" }
        });
        return response;
    } catch (error) {
        console.error("Error signing up:", error);
        throw error;
    }
};

/**
 * ✅ Confirm user account with the verification code
 * @param email - User's email
 * @param code - The verification code received via email/SMS
 */
export const confirmSignUp = async (email: string, code: string) => {
    try {
        await Auth.confirmSignUp(email, code);
        return true;
    } catch (error) {
        console.error("Error confirming sign-up:", error);
        throw error;
    }
};

/**
 * ✅ Resend confirmation code for email verification
 * @param email - User's email
 */
export const resendConfirmationCode = async (email: string) => {
    try {
        await Auth.resendSignUp(email);
        return true;
    } catch (error) {
        console.error("Error resending confirmation code:", error);
        throw error;
    }
};


/**
 * ✅ Complete the NEW_PASSWORD_REQUIRED challenge
 * @param user - The Cognito user object returned by Auth.signIn()
 * @param newPassword - The new password chosen by the user
 */
export const completeNewPassword = async (user: any, newPassword: string) => {
    try {
        const updatedUser = await Auth.completeNewPassword(user, newPassword);

        // 🎟️ Store tokens after password change
        const session = updatedUser.getSignInUserSession();
        if (session) {
            const accessToken = session.getAccessToken().getJwtToken();
            const refreshToken = session.getRefreshToken().getToken();
            const idToken = session.getIdToken().getJwtToken();
            await AsyncStorage.setItem("accessToken", accessToken);
            await AsyncStorage.setItem("refreshToken", refreshToken);
            await AsyncStorage.setItem("idToken", idToken);
        }

        return updatedUser;
    } catch (error) {
        console.error("Error completing new password challenge:", error);
        throw error;
    }
};


/**
 * ✅ Sign in user (handles Cognito NEW_PASSWORD_REQUIRED)
 * @param email - User's email
 * @param password - User's password
 */
export const signIn = async (email: string, password: string) => {
    try {
        const user = await Auth.signIn(email, password);

        // 👇 Handle challenge: NEW_PASSWORD_REQUIRED
        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            console.warn("⚠️ User must set a new password before continuing.");
            return {
                challenge: "NEW_PASSWORD_REQUIRED",
                user, // Return user object (required for completeNewPassword)
            };
        }

        // ✅ Normal login flow
        const session = user.getSignInUserSession();
        if (session) {
            const accessToken = session.getAccessToken().getJwtToken();
            const refreshToken = session.getRefreshToken().getToken();
            const idToken = session.getIdToken().getJwtToken();
            await AsyncStorage.setItem("accessToken", accessToken);
            await AsyncStorage.setItem("refreshToken", refreshToken);
            await AsyncStorage.setItem("idToken", idToken);
        }

        return { user, challenge: null };
    } catch (error) {
        console.error("Error signing in:", error);
        throw error;
    }
};


/**
 * ✅ Refresh the ID token if it's expired
 */
export const refreshIdTokenIfNeeded = async (): Promise<string | null> => {
    try {
        let storedIdToken = await AsyncStorage.getItem("idToken");

        if (!storedIdToken) {
            console.warn("No ID token found. Attempting to retrieve a new session...");
            try {
                const session = await Auth.currentSession();
                const newIdToken = session.getIdToken().getJwtToken();
                await AsyncStorage.setItem("idToken", newIdToken);
                return newIdToken;
            } catch (sessionError) {
                console.error("Failed to retrieve session:", sessionError);
                return null;
            }
        }

        // Ensure token is in a valid Base64 format
        const cleanedToken = storedIdToken.replace(/-/g, "+").replace(/_/g, "/");

        const decodedToken = decodeJWT(cleanedToken) as { exp: number };
        const currentTime = Math.floor(Date.now() / 1000);
        const expiresIn = decodedToken.exp - currentTime;

        // Refresh if token expires in < 5 minutes
        if (expiresIn < 300) {
            console.log("Refreshing ID token...");
            const session = await Auth.currentSession();
            const newIdToken = session.getIdToken().getJwtToken();
            await AsyncStorage.setItem("idToken", newIdToken);
            return newIdToken;
        }

        return storedIdToken;
    } catch (error) {
        console.error("Error refreshing ID token:", error);
        return null;
    }
};

/**
 * ✅ Sign out user
 */
export const signOut = async () => {
    try {
        await Auth.signOut();
        await AsyncStorage.removeItem("accessToken");
        await AsyncStorage.removeItem("refreshToken");
        return true;
    } catch (error) {
        console.error("Error signing out:", error);
        throw error;
    }
};

/**
 * ✅ Initiate password reset (forgot password)
 * @param email - User's email
 */
export const forgotPassword = async (email: string) => {
    try {
        await Auth.forgotPassword(email);
        return true;
    } catch (error) {
        console.error("Error initiating password reset:", error);
        throw error;
    }
};

/**
 * ✅ Confirm new password after reset
 * @param email - User's email
 * @param code - Code received via email/SMS
 * @param newPassword - New password
 */
export const confirmNewPassword = async (email: string, code: string, newPassword: string) => {
    try {
        await Auth.forgotPasswordSubmit(email, code, newPassword);
        return true;
    } catch (error) {
        console.error("Error confirming new password:", error);
        throw error;
    }
};

/**
 * ✅ Get the current authenticated user
 */
export const getCurrentUser = async () => {
    try {
        const user = await Auth.currentAuthenticatedUser();
        return user;
    } catch (error) {
        console.error("Error fetching current user:", error);
        return null;
    }
};

/**
 * ✅ Retrieve user session & token
 */
export const getSession = async () => {
    try {
        const session = await Auth.currentSession();
        return {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
        };
    } catch (error) {
        console.error("Error getting session:", error);
        return null;
    }
};

/**
 * ✅ Update user attributes
 */
export const updateUserAttributes = async (attributes: Record<string, string>) => {
    try {
        const session = await getSession();
        if (!session) throw new Error("Session not available");

        const user = await Auth.currentAuthenticatedUser();
        return await Auth.updateUserAttributes(user, attributes);
    } catch (error) {
        console.error("❌ Error updating user attributes:", error);
        throw error;
    }
};

const decodeJWT = (token: string) => {
    const [header, payload, signature] = token.split(".");
    const base64Payload = Buffer.from(payload, "base64").toString();
    return JSON.parse(base64Payload);
};

/**
 * ✅ Refresh the token if it's expired
 */
export const refreshTokenIfNeeded = async () => {
    try {
        const storedToken = await AsyncStorage.getItem("accessToken");
        if (!storedToken) return null;

        // Ensure token is in a valid Base64 format
        const cleanedToken = storedToken.replace(/-/g, "+").replace(/_/g, "/");

        const decodedToken = decodeJWT(cleanedToken) as { exp: number };
        const currentTime = Math.floor(Date.now() / 1000);
        const expiresIn = decodedToken.exp - currentTime;

        //console.log(`Token expires in: ${expiresIn} seconds`);

        // Refresh if token expires in < 5 minutes
        if (expiresIn < 300) {
            console.log("Refreshing token...");
            const session = await Auth.currentSession();
            const newAccessToken = session.getAccessToken().getJwtToken();
            await AsyncStorage.setItem("accessToken", newAccessToken);
            return newAccessToken;
        }

        return storedToken;
    } catch (error) {
        console.error("Error refreshing token:", error);
        return null;
    }
};

/**
 * ✅ Check if user is authenticated
 */
export const isAuthenticated = async () => {
    try {
        const session = await Auth.currentSession();
        return session ? true : false;
    } catch (error) {
        return false;
    }
};


/**
 * ✅ Delete User Account
 */
export const deleteUserAccount = async (username: string, token: string) => {
    try {
        await API.Delete(username, token);
        await Auth.deleteUser();
        await AsyncStorage.clear(); // Clears local storage upon logout
        return true;
    } catch (error) {
        console.error("Error deleting user:", error);
        throw error;
    }
};

